import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Navigate } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Shield, AppWindow, ClipboardList, Settings,
  Bug, Heart, AlertTriangle, Lock,
} from 'lucide-react'
import { GuardianAppsTab } from '@/components/guardian/guardian-apps-tab'
import { GuardianAuditTab } from '@/components/guardian/guardian-audit-tab'
import { GuardianSettingsTab } from '@/components/guardian/guardian-settings-tab'
import { GuardianScanBanner } from '@/components/guardian/guardian-scan-banner'
import { guardianService, type GuardianPortfolioHealth } from '@/services/guardian'
import { useRoleAccess } from '@/hooks/useRoleAccess'

const VALID_TABS = ['apps', 'audit', 'settings'] as const

export function GuardianPage() {
  const { hasAdminAccess } = useRoleAccess()

  // Admin-only feature gate (admin role only; org owners are NOT admins)
  if (!hasAdminAccess()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4">
        <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center">
          <Lock className="h-8 w-8 text-slate-400" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">Access Restricted</h2>
        <p className="text-slate-500 max-w-md">Guardian is currently available to administrators only. Contact your organization admin for access.</p>
      </div>
    )
  }
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    return tabParam && VALID_TABS.includes(tabParam as typeof VALID_TABS[number]) ? tabParam : 'apps'
  })
  const [health, setHealth] = useState<GuardianPortfolioHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    guardianService.getPortfolioHealth()
      .then(setHealth)
      .catch(e => console.error('Failed to load health:', e))
      .finally(() => setLoading(false))
  }, [refreshKey])

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab)
    const newParams = new URLSearchParams(searchParams)
    if (tab === 'apps') {
      newParams.delete('tab')
    } else {
      newParams.set('tab', tab)
    }
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  const handleScanComplete = useCallback(() => {
    setRefreshKey(k => k + 1)
  }, [])

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center shadow-sm">
          <Shield className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Guardian</h1>
          <p className="text-sm text-slate-500">Dependency monitoring and update automation</p>
        </div>
      </div>

      {/* Stats Bar */}
      {!loading && health && health.active_apps > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <AppWindow className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{health.active_apps}</p>
                <p className="text-sm text-slate-500">Apps Monitored</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Bug className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{health.total_findings}</p>
                <p className="text-sm text-slate-500">Findings</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{health.critical + health.high}</p>
                <p className="text-sm text-slate-500">Critical + High</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Heart className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{health.health_score != null ? `${health.health_score}/100` : '—'}</p>
                <p className="text-sm text-slate-500">Health Score</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      )}

      {/* Scan Progress Banner */}
      <GuardianScanBanner onScanComplete={handleScanComplete} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="apps" className="gap-1.5">
            <AppWindow className="h-4 w-4" />
            <span className="hidden sm:inline">Apps</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Historical Audit</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-1.5">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="apps" className="mt-6">
          <GuardianAppsTab key={refreshKey} health={health} />
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <GuardianAuditTab />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <GuardianSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
